import React, { FC, useEffect } from 'react';

import { useIntl } from 'react-intl';

import { IItem } from '@rbi-ctg/menu';
import { Calories } from 'components/price-and-calories';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { useProductPrice } from 'hooks/menu/use-product-price';
import { computeProductCalories } from 'hooks/menu/utils';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMenuContext } from 'state/menu';
import { useProductWizardContext } from 'state/product-wizard';
import { useUIContext } from 'state/ui';
import { RBIBrand, brand, getCountry } from 'utils/environment';
import { EventName, emitEvent } from 'utils/event-hub';
import { isCombo } from 'utils/menu';

import { PricePerUnit } from '../price-per-unit/price-per-unit';
import { PriceWithoutTax } from '../price-without-tax/price-without-tax';

import { DepositPriceText, PriceAndCaloriesText } from './price-and-calories.styled';

export const PriceAndCalories: FC = () => {
  const { showStaticMenu } = useMenuContext();
  const { formatMessage } = useIntl();
  const { formatCurrencyForLocale } = useUIContext();
  const { formattedProductPrice, totalPrice, depositPrice, isdepositIncluded } = useProductPrice({
    shouldIncludeModifications: false,
    shouldIncludeQuantity: false,
  });
  const { selectedProduct, menuObject } = useProductWizardContext();
  const { formatCalories } = useMenuCalories();
  const hasDepositIncluded = isdepositIncluded() || false;
  const isProductCombo = isCombo(selectedProduct);
  const isCaloriesCalculatorEnabled = useFlag(LaunchDarklyFlag.ENABLE_CALORIES_CALCULATOR);
  const shouldComputeCalories =
    isCaloriesCalculatorEnabled && !!selectedProduct && !selectedProduct?.hideCalories;
  const productCalories = shouldComputeCalories ? computeProductCalories(selectedProduct) : 0;
  const formattedProductCalories = formatCalories(productCalories);
  const enableTaxLawBKAR = useFlag(LaunchDarklyFlag.ENABLE_TAX_LAW_BK_AR);
  const enableGermanLegalRequirement = useFlag(LaunchDarklyFlag.ENABLE_GERMAN_LEGAL_REQUIREMENT);
  const isShowingPriceWithoutTax =
    brand() === RBIBrand.BK && getCountry().toLocaleLowerCase() === 'ar' && enableTaxLawBKAR;
  const depositText = menuObject?.depositInfoText?.locale || '';
  useEffect(() => {
    if (!formattedProductCalories) {
      return;
    }
    // Emit event for when calories are loaded since menu performance tracking uses this
    emitEvent(EventName.MENU_ITEM_CALORIES_LOADED);
  }, [formattedProductCalories]);

  return (
    <PriceAndCaloriesText>
      {!showStaticMenu && <span data-testid="product-total-price">{formattedProductPrice}</span>}
      {isCaloriesCalculatorEnabled && (
        <Calories hideCalories={!!selectedProduct?.hideCalories}>
          <span data-testid="product-total-calories">
            {!showStaticMenu && <span> • </span>}
            {formattedProductCalories}
          </span>
        </Calories>
      )}

      {selectedProduct && (
        <>
          {enableGermanLegalRequirement && !isProductCombo && hasDepositIncluded && (
            <DepositPriceText>
              <span data-testid="product-total-pfand">
                {formatMessage(
                  { id: 'pfand' },
                  {
                    price: formatCurrencyForLocale(depositPrice),
                  }
                )}
              </span>
            </DepositPriceText>
          )}

          <PricePerUnit
            isLegalRequirement={enableGermanLegalRequirement}
            product={selectedProduct as IItem}
            totalAmount={totalPrice()}
            formattedPrice={formattedProductPrice}
          />

          {enableGermanLegalRequirement &&
            isProductCombo &&
            depositText.length > 0 &&
            hasDepositIncluded &&
            totalPrice() > 0 && <DepositPriceText>{depositText}</DepositPriceText>}

          {isShowingPriceWithoutTax && <PriceWithoutTax totalAmount={totalPrice()} />}
        </>
      )}
    </PriceAndCaloriesText>
  );
};
